import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { CREATE_PACK, GET_PACK_BY_SELLER } from "../../api/pack-queries";
import { useNavigate } from "react-router-dom";
import Header from "../../../../components/common/header";
import clsx from "clsx";
import { getCDNResizedImageUrl } from "../../../../utils/get-cdn-resized-image-url";
import { track } from "@amplitude/analytics-browser";
import { roundUpToHundred } from "../../../../utils/price";

const SignupPackManagement: React.FC = () => {
  const navigate = useNavigate();
  const [packType, setPackType] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("10000");
  const [quantity, setQuantity] = useState("");
  const [discountRate, setDiscountRate] = useState(0.6);

  const [createPack] = useMutation(CREATE_PACK);
  const { loading, error } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
  });

  useQuery(GET_PACK_BY_SELLER, {
    onCompleted: (data) => {
      if (data.packBySeller) {
        const pack = data.packBySeller;
        setPackType(pack.category);
        setDescription(pack.description);
        setPrice(pack.price.toString());
        setQuantity(pack.quantity.toString());

        // 할인율 설정 (discountRate가 백분율로 저장되어 있다면 소수로 변환)
        if (pack.discountRate) {
          const rate = pack.discountRate / 100; // 백분율을 소수로 변환
          if (rate === 0.55 || rate === 0.6) {
            setDiscountRate(rate);
          } else {
            // 기존 할인율이 55% 또는 60%가 아니면 기본값 60%로 설정
            setDiscountRate(0.6);
          }
        }
      }
    },
    fetchPolicy: "network-only",
  });

  if (loading) return <></>;
  if (error) return <></>;

  const handleSubmit = () => {
    const missingFields = [];

    if (!packType) missingFields.push("[팩 종류]");
    if (!description) missingFields.push("[설명]");
    if (!price) missingFields.push("[가격]");
    if (!quantity) missingFields.push("[개수]");

    if (missingFields.length > 0) {
      alert(`${missingFields.join("\n- ")}\n항목이 입력되지 않았어요.`);
      return;
    }

    // 할인율을 백분율로 변환 (discountRate는 소수값 0.55 또는 0.6)
    const discountRatePercent = Math.floor(discountRate * 100); // 55 또는 60
    const roundedDiscountPrice = roundUpToHundred(
      Math.round(parseFloat(price) * (1 - discountRate))
    );
    const userPrice = roundedDiscountPrice + 900;

    createPack({
      variables: {
        name: packType,
        category: packType,
        price: parseFloat(price),
        discountPrice: roundedDiscountPrice,
        discountRate: discountRatePercent, // 백분율로 전송 (55 또는 60)
        userPrice: userPrice,
        quantity: parseInt(quantity),
        description: description,
      },
      onCompleted: () => {
        track("사장님 입점신청8 예약판매 팩 등록 완료");
        navigate("/boss/signup/reserve-pack/pickup");
      },
    });
  };

  return (
    <div className="flex flex-col min-h-screen h-full bg-white">
      <Header title="입점 신청" />

      <div className="flex flex-col p-4">
        <div className="w-full space-y-6">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">예약 판매 설정</h1>
            <div className="bg-gray-100 rounded-md p-4">
              <p className="text-gray-700">
                등록하신 정보는 입점 완료 후 수정 가능하니 편하게 입력해주세요.
              </p>
            </div>
          </div>

          <div>
            <div className="space-y-2 pb-2">
              <label className="text-black font-semibold text-xl">
                팩 종류
              </label>

              <div className="flex justify-center">
                <img
                  src={getCDNResizedImageUrl(
                    "https://s3.ap-northeast-2.amazonaws.com/cdn-mahi.com/250326-reserve-pack-onboarding3.jpg",
                    {
                      width: 600,
                    }
                  )}
                  alt="reserve-pack"
                  className="w-1/2 rounded-xl"
                />
              </div>

              <p className="text-gray-800 mb-2 text-center">
                어떤 음식을 담나요?
              </p>
            </div>
            <div className="flex flex-col space-y-2">
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="베이커리"
                  checked={packType === "베이커리"}
                  onChange={(e) => setPackType(e.target.value)}
                  className="mr-2"
                />
                베이커리
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="식사류"
                  checked={packType === "식사류"}
                  onChange={(e) => setPackType(e.target.value)}
                  className="mr-2"
                />
                식사류
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="샐러드"
                  checked={packType === "샐러드"}
                  onChange={(e) => setPackType(e.target.value)}
                  className="mr-2"
                />
                샐러드
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="정육점"
                  checked={packType === "정육점"}
                  onChange={(e) => setPackType(e.target.value)}
                  className="mr-2"
                />
                정육점
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="반찬과 요리"
                  checked={packType === "반찬과 요리"}
                  onChange={(e) => setPackType(e.target.value)}
                  className="mr-2"
                />
                반찬과 요리
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="기타"
                  checked={packType === "기타"}
                  onChange={(e) => setPackType(e.target.value)}
                  className="mr-2"
                />
                기타
              </label>
            </div>
          </div>

          <div className="mb-8">
            <label className="text-black font-semibold text-xl">설명</label>
            <p className="text-gray-800">어떤 메뉴가 주로 담기나요?</p>
            <p className="text-green-600 mb-2 font-medium">
              여기 설명을 자세하게 적어주시면 작성하지 않았을 때에 비해 판매율이
              15% 정도 올라요!
            </p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full h-36 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
              placeholder="예시) 매일 빵을 굽는 0000입니다.  샌드위치류, 크림소금빵류가 주로 올라옵니다. 당일 제조 이기 때문에 냉장에서 3일까지 드실 수 있습니다.  고객님들은 저렴한 가격에 드실 수 있고, 저희는 버리는 음식이 줄어들어 기쁩니다!"
            />
          </div>

          {/* 할인율 선택 */}
          <div className="mb-8">
            <label className="block text-black font-semibold text-xl mb-2">
              할인율 선택
            </label>
            <p className="text-gray-800 mb-2">
              고객에게 판매할 할인율을 선택해주세요
            </p>
            <div className="flex flex-col space-y-2">
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="0.55"
                  checked={discountRate === 0.55}
                  onChange={(e) => setDiscountRate(0.55)}
                  className="mr-2"
                />
                55% 할인
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="0.6"
                  checked={discountRate === 0.6}
                  onChange={(e) => setDiscountRate(0.6)}
                  className="mr-2"
                />
                60% 할인
              </label>
            </div>
          </div>

          <div className="mb-8">
            <label className="text-black font-semibold text-xl">가격</label>

            <p className="text-gray-800">
              팩에 얼마만큼의 정가상품을 담을까요?
              <br />
            </p>
            <div className="flex flex-col space-y-2">
              <label className="flex flex-col rounded-lg p-2">
                <input
                  type="range"
                  min="4000"
                  max="20000"
                  step="1000"
                  value={price || "10000"}
                  onChange={(e) => setPrice(e.target.value)}
                  className="mr-2 w-full my-4"
                />
                <span className="font-bold">
                  정가{" "}
                  <span className="text-green-500">
                    {(parseFloat(price) || 10000).toLocaleString()}원
                  </span>{" "}
                  어치를 랜덤으로 담을게요!
                </span>
              </label>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-black font-semibold text-xl">개수</label>
            <p className="text-gray-800 mb-2">
              <span className="text-green-500 font-medium bg-gray-50 rounded p-1">
                정가 {price.toLocaleString()}원
              </span>
              인 팩을 손님이 하루에 몇 개 구매할 수 있게 설정할까요?
            </p>
            <div className="flex flex-col space-y-2">
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="2"
                  checked={quantity === "2"}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="mr-2"
                />
                하루에 2개
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="3"
                  checked={quantity === "3"}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="mr-2"
                />
                하루에 3개
              </label>
              <label className="flex items-center border border-gray-300 rounded-lg p-2 bg-gray-100">
                <input
                  type="radio"
                  value="4"
                  checked={quantity === "4"}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="mr-2"
                />
                하루에 4개
              </label>
            </div>
          </div>

          <div>
            <button
              onClick={handleSubmit}
              className={clsx(
                "w-full py-3 text-white font-semibold rounded-xl transition duration-150",
                "hover:brightness-90 active:brightness-90 bg-primary"
              )}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPackManagement;
